import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accountsApi } from '../../api/accounts';
import { formatMoney } from '../../utils/formatMoney';
import { today } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import DatePickerInput from '../../components/DatePickerInput';

export default function TrialBalance() {
  const currentYear = new Date().getFullYear();
  const [dateFrom, setDateFrom] = useState(`${currentYear}-01-01`);
  const [dateTo, setDateTo] = useState(today());
  const [nonZeroOnly, setNonZeroOnly] = useState(true);

  const { data: balances, isLoading } = useQuery({
    queryKey: ['trial-balance', dateFrom, dateTo, nonZeroOnly],
    queryFn: () => accountsApi.trialBalance({ from: dateFrom, to: dateTo, nonZeroOnly }),
  });

  if (isLoading) return <LoadingSpinner />;

  const totalDebit = balances?.reduce((a, b) => a + b.debitTotal, 0) ?? 0;
  const totalCredit = balances?.reduce((a, b) => a + b.creditTotal, 0) ?? 0;
  const totalBalance = balances?.reduce((a, b) => a + b.balance, 0) ?? 0;

  // Group by class
  const grouped = (balances ?? []).reduce<Record<string, typeof balances>>((acc, b) => {
    const cls = String(b.accountNumber).charAt(0);
    if (!acc[cls]) acc[cls] = [];
    acc[cls].push(b);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Trial Balance</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-end">
        <DatePickerInput
          label="From"
          value={dateFrom}
          onChange={setDateFrom}
          inputClassName="w-36"
        />
        <DatePickerInput
          label="To"
          value={dateTo}
          onChange={setDateTo}
          inputClassName="w-36"
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={nonZeroOnly}
            onChange={(e) => setNonZeroOnly(e.target.checked)}
          />
          Non-zero only
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="dense w-full">
          <thead>
            <tr>
              <th className="text-right">Account</th>
              <th className="text-left">Name</th>
              <th className="text-right">Debit Total</th>
              <th className="text-right">Credit Total</th>
              <th className="text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {(['1', '2', '3', '4', '5', '6'] as const).map((cls) => {
              const group = grouped[cls];
              if (!group || group.length === 0) return null;

              const classDebit = group.reduce((a, b) => a + b.debitTotal, 0);
              const classCredit = group.reduce((a, b) => a + b.creditTotal, 0);
              const classBalance = group.reduce((a, b) => a + b.balance, 0);

              return (
                <>
                  {group.map((b) => (
                    <tr key={b.accountNumber} className="border-b border-gray-50">
                      <td className="num">{b.accountNumber}</td>
                      <td>{b.accountName}</td>
                      <td className="num">{formatMoney(b.debitTotal)}</td>
                      <td className="num">{formatMoney(b.creditTotal)}</td>
                      <td className="num font-medium">{formatMoney(b.balance)}</td>
                    </tr>
                  ))}
                  <tr key={`class-${cls}`} className="bg-gray-50 font-semibold">
                    <td colSpan={2} className="text-right">Class {cls} Total</td>
                    <td className="num">{formatMoney(classDebit)}</td>
                    <td className="num">{formatMoney(classCredit)}</td>
                    <td className="num">{formatMoney(classBalance)}</td>
                  </tr>
                </>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-primary-50 font-bold">
              <td colSpan={2} className="text-right">Grand Total</td>
              <td className="num">{formatMoney(totalDebit)}</td>
              <td className="num">{formatMoney(totalCredit)}</td>
              <td className="num">{formatMoney(totalBalance)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">Total Debit</div>
          <div className="text-lg font-bold num">{formatMoney(totalDebit)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">Total Credit</div>
          <div className="text-lg font-bold num">{formatMoney(totalCredit)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">Balance</div>
          <div className={`text-lg font-bold num ${Math.abs(totalBalance) < 0.005 ? 'text-green-600' : 'text-red-600'}`}>
            {formatMoney(totalBalance)}
          </div>
        </div>
      </div>
    </div>
  );
}
